import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { PieChart, ChefHat, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API}/api` });

const CATEGORIES = {
  entree: 'Entrees',
  tapas: 'Tapas',
  plat_viande: 'Viandes',
  plat_poisson: 'Poissons',
  dessert: 'Desserts',
};

const ChefCouts = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/marges/dashboard');
      setData(res.data);
    } catch {
      toast.error('Erreur chargement');
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const ratioColor = (ratio, hasCost) => {
    if (!hasCost) return 'text-[rgb(var(--lm-text-muted))]';
    if (ratio <= 25) return 'text-green-400';
    if (ratio <= 30) return 'text-lemas-green';
    if (ratio <= 35) return 'text-orange-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="couts-loading">
        <RefreshCw size={24} className="animate-spin text-lemas-green" />
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const dishes = data?.dishes || [];
  const filtered = catFilter === 'all' ? dishes : dishes.filter(d => d.categorie === catFilter);

  // Group by category
  const grouped = {};
  filtered.forEach(d => {
    const cat = d.categorie || 'autre';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(d);
  });

  return (
    <div className="space-y-4" data-testid="chef-couts">
      {/* Header */}
      <div>
        <h2 className="font-title text-xl text-[rgb(var(--lm-text))] flex items-center gap-2">
          <PieChart size={20} className="text-lemas-green" /> Couts des plats
        </h2>
        <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-1">
          {kpis.plats_avec_fiche || 0} fiches sur {kpis.total_plats || 0} plats
          {kpis.ratio_moyen > 0 && ` — Ratio moyen: ${kpis.ratio_moyen}%`}
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-[rgb(var(--lm-surface))]" data-testid="chef-kpi-ratio">
          <CardContent className="p-4 text-center">
            <p className="text-[10px] text-[rgb(var(--lm-text-secondary))] uppercase">Ratio moyen</p>
            <p className={`font-mono text-2xl mt-1 ${ratioColor(kpis.ratio_moyen, kpis.ratio_moyen > 0)}`}>
              {kpis.ratio_moyen > 0 ? `${kpis.ratio_moyen}%` : '—'}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-[rgb(var(--lm-surface))] border-green-400/20" data-testid="chef-kpi-best">
          <CardContent className="p-4 text-center">
            <p className="text-[10px] text-green-400 uppercase">Meilleur</p>
            <p className="text-sm text-green-400 mt-1 truncate">{kpis.meilleur_plat?.nom || '—'}</p>
          </CardContent>
        </Card>
        <Card className={`bg-[rgb(var(--lm-surface))] ${kpis.nb_alertes > 0 ? 'border-red-400/20' : ''}`} data-testid="chef-kpi-alerts">
          <CardContent className="p-4 text-center">
            <p className="text-[10px] text-[rgb(var(--lm-text-secondary))] uppercase">Alertes</p>
            <p className={`font-mono text-2xl mt-1 ${kpis.nb_alertes > 0 ? 'text-red-400' : 'text-lemas-green'}`}>
              {kpis.nb_alertes || 0}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Category filters */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setCatFilter('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${catFilter === 'all' ? 'bg-lemas-green/20 text-lemas-green' : 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-secondary))]'}`}
          data-testid="chef-cat-all">
          Tous ({dishes.length})
        </button>
        {Object.entries(CATEGORIES).map(([cat, label]) => {
          const count = dishes.filter(d => d.categorie === cat).length;
          if (!count) return null;
          return (
            <button key={cat} onClick={() => setCatFilter(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${catFilter === cat ? 'bg-lemas-green/20 text-lemas-green' : 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-secondary))]'}`}
              data-testid={`chef-cat-${cat}`}>
              {label} ({count})
            </button>
          );
        })}
      </div>

      {/* Dishes by category */}
      {Object.entries(grouped).map(([cat, catDishes]) => (
        <Card key={cat} className="bg-[rgb(var(--lm-surface))]" data-testid={`chef-cat-card-${cat}`}>
          <CardContent className="p-4">
            <h3 className="text-sm text-lemas-green font-medium mb-3">{CATEGORIES[cat] || cat}</h3>
            <div className="space-y-2">
              {catDishes.map(dish => (
                <div key={dish.id} className={`flex items-center justify-between py-2 px-3 rounded-lg ${dish.alerte ? 'bg-red-400/5' : 'bg-lemas-bg/30'}`}
                  data-testid={`chef-dish-${dish.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-[rgb(var(--lm-text))] truncate">{dish.nom}</p>
                      {dish.alerte && <AlertTriangle size={12} className="text-red-400 shrink-0" />}
                    </div>
                    {dish.a_fiche && (
                      <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{dish.ingredients?.length || 0} ingredients</p>
                    )}
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <p className="font-mono text-xs text-[rgb(var(--lm-text-secondary))]">Cout</p>
                      <p className="font-mono text-sm text-[rgb(var(--lm-text))]">{dish.cout_matiere > 0 ? `${dish.cout_matiere.toFixed(2)}` : '—'}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-xs text-[rgb(var(--lm-text-secondary))]">Ratio</p>
                      <p className={`font-mono text-sm font-medium ${ratioColor(dish.ratio_matiere, dish.cout_matiere > 0)}`}>
                        {dish.cout_matiere > 0 ? `${dish.ratio_matiere}%` : '—'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {filtered.length === 0 && (
        <Card className="bg-[rgb(var(--lm-surface))]">
          <CardContent className="py-12 text-center">
            <ChefHat size={40} className="mx-auto text-[rgb(var(--lm-text-muted))] mb-3" />
            <p className="text-[rgb(var(--lm-text-secondary))]">Aucun plat</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ChefCouts;
